import { Link } from "react-router";
import { RowMenu, type RowMenuItem } from "./RowMenu.jsx";

/**
 * 見出しの右の操作（#172、docs/10 §5.12・docs/67 §4）：状態チップの右に「主なボタン1つ」と「⋯」のメニュー。
 *   帳票の見出し（見積書・請求書）は 1280px の幅でも1段に収める＝ボタンを並べない。主なボタンは rank のいちばん小さいもの、
 *   ほかは「⋯」の中（項目の testId はボタンのときと同じ＝⋯ を開けば同じ名前で押せる）。
 *   主なボタンは脇役の .small（藍＝.ai。消す動き danger は枠だけ）。画面の右端の主のボタン（発行・PDF を表示）は別
 * #173（docs/67 §5・docs/10 §5.12）：**見出しのメニューは「⋯」の1つ**。見積書の「≡」（PDF・見積依頼・削除）もここに入れた。
 *   「⋯」の中は区切り線で3段＝ state（状態の操作：訂正版を作成・取消・請求書の動き・入金）／ doc（PDF・見積依頼・写真台帳）／ danger（削除・朱）。
 *   段の中は渡した順。区切り線は段の頭にだけ引く（渡した項目の divider は使わない）
 */
export type HeadActionGroup = "state" | "doc" | "danger";
export type HeadAction = RowMenuItem & {
  /** 主なボタンの候補の順（小さいほど先）。付けないものは「⋯」にだけ入る */
  rank?: number;
  /** 「⋯」の中の段（既定は state） */
  group?: HeadActionGroup;
};

const GROUPS: readonly HeadActionGroup[] = ["state", "doc", "danger"];

/** 主なボタン1つと、「⋯」に入れる残り（段の順に並べ、段の頭に区切り線） */
export function splitHeadActions(actions: readonly HeadAction[]): { main: HeadAction | null; more: HeadAction[] } {
  let main: HeadAction | null = null;
  for (const a of actions) if (a.rank != null && !a.disabled && !a.sub && (main == null || a.rank < (main.rank ?? Infinity))) main = a;
  const rest = actions.filter((a) => a !== main);
  const more: HeadAction[] = [];
  for (const g of GROUPS) {
    rest
      .filter((a) => (a.group ?? "state") === g)
      .forEach((a, i) => more.push({ ...a, divider: i === 0 && more.length > 0 }));
  }
  return { main, more };
}

/** 横の「⋯」（見積の行のメニューと同じ形） */
export const DOTS_H = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

/**
 * primary：主なボタンを塗りのボタン（.btn.primary 44px）にする（見積依頼書の「送付」＝Figma ②改 10/20。ほかの画面は今までの形）
 * part（#243、docs/94 B-6）：⋯ はいつも頭の右端。主なボタンと ⋯ の間に画面の主ボタン（発行・PDF 表示）があるときは、
 *   主なボタンを置く所で part="main"、右端で part="more" と2回描く（同じ actions を渡す）
 */
export function HeadActions({ actions, label, primary = false, part }: { actions: readonly HeadAction[]; label: string; primary?: boolean; part?: "main" | "more" }) {
  const split = splitHeadActions(actions);
  const main = part === "more" ? null : split.main;
  const more = part === "main" ? [] : split.more;
  if (!main && !more.length) return null;
  const cls = primary && !main?.danger ? "btn primary" : "small" + (main?.danger ? "" : " ai");
  return (
    <>
      {main ? (
        main.to ? (
          <Link className={cls} to={main.to} title={main.title} data-testid={main.testId} data-leave={main.onLink ? "own" : undefined} onClick={(e) => main.onLink?.(e)}>
            {main.label}
          </Link>
        ) : (
          <button type="button" className={cls} title={main.title} data-testid={main.testId} onClick={() => main.onSelect?.()}>
            {main.label}
          </button>
        )
      ) : null}
      {more.length ? <RowMenu label={label} items={more} testId="state-more" trigger={DOTS_H} triggerClass="small hmore" /> : null}
    </>
  );
}
